import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Row } from "reactstrap";
import ataEncaminhamentosService from "services/AtaEncaminhamentosService";
import ModalExclude from "./ModalExclude";
import ModalCreateEdit from "./ModalCreateEdit";
import moment from "moment";
import ModalChangePrazo from "./ModalChangePrazo";
import ModalFinish from "./ModalFinish";
import Loading from "components/Common/Loading";
import { useNavigate } from "react-router-dom";
import MaterialTableContainer from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {
	PencilSimple,
	CalendarBlank,
	Check,
	Trash,
	Info,
	Printer,
	Plus,
	ArrowsClockwise,
} from "@phosphor-icons/react";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
	getFilterFieldProps,
} from "components/Common/MaterialTableStyles";

const Encaminhamentos = (props) => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		inserido: { min: 95, max: 95 },        // Largura mín/máx da coluna "Inserido" (em pixels)
		encaminhamentos: { min: 150 },          // Largura mínima da coluna "Encaminhamentos" (sem máximo - cresce livremente)
		responsavel: { min: 120, max: 120 },    // Largura mín/máx da coluna "Responsável" (em pixels)
		prazo: { min: 100, max: 100 },          // Largura mín/máx da coluna "Prazo" (em pixels)
		status: { min: 80, max: 80 },          // Largura mín/máx da coluna "Status" (em pixels)
		actions: { min: 70, max: 70 },         // Largura mín/máx da coluna "Actions" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [modal, setModal] = useState();
	const [showModal, setShowModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [modalChangePrazo, setModalChangePrazo] = useState(false);
	const [changePrazo, setChangePrazo] = useState();
	const [showModalFininsh, setShowModalFininsh] = useState(false);
	const [dataModalFinish, setDataModalFinish] = useState();
	const [isLoading, setIsLoading] = useState(false);
	const id = props?.dataId;
	const podeVisualizar = props?.podeVisualizar;
	const [dataInTable, setDataInTable] = useState(null);
	const [isFullScreen, setIsFullScreen] = useState(false);

	const navigate = useNavigate();

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	const columns = useMemo(
		() => [
			{
				header: "Inserido",
				accessorKey: "create_at",
				accessorFn: (originalRow) =>
					moment(originalRow.create_at).startOf("day"),
				filterVariant: "date",
				widthPixels: getColumnWidth("inserido"),
				minWidthPixels: COLUMN_WIDTHS.inserido.min,
				maxWidthPixels: COLUMN_WIDTHS.inserido.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ cell }) => (
					<span style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
					}}>
						{moment(cell.getValue()).format("DD/MM/YYYY")}
					</span>
				),
			},
			{
				accessorKey: "referrals",
				header: "Encaminhamentos",
				filterFn: "contains",
				widthPixels: getColumnWidth("encaminhamentos"),
				minWidthPixels: COLUMN_WIDTHS.encaminhamentos.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell, row }) => (
					<>
						{podeVisualizar ? (
							<span
								style={{
									cursor: "pointer",
									display: "block", // Permite que o texto quebre corretamente
									maxWidth: "100%", // Respeita o tamanho da célula
									wordBreak: "break-word", // Quebra o texto se for muito grande
									overflowWrap: "break-word", // Compatibilidade extra para quebra de palavras
									padding: "3px",
								}}
								onMouseEnter={(e) => {
									e.target.style.textDecoration = "underline";
								}}
								onMouseLeave={(e) => {
									e.target.style.textDecoration = "none";
								}}
								onClick={() => editarItem(row.original)}
							>
								{cell.getValue()}
							</span>
						) : (
							<span
								style={{
									display: "block", // Permite que o texto quebre corretamente
									maxWidth: "100%", // Respeita o tamanho da célula
									wordBreak: "break-word", // Quebra o texto se for muito grande
									overflowWrap: "break-word", // Compatibilidade extra para quebra de palavras
									padding: "3px",
								}}
							>
								{cell.getValue()}
							</span>
						)}
					</>
				),
			},
			{
				accessorKey: "responsible.lastname",
				header: "Responsável",
				filterFn: "contains",
				widthPixels: getColumnWidth("responsavel"),
				minWidthPixels: COLUMN_WIDTHS.responsavel.min,
				maxWidthPixels: COLUMN_WIDTHS.responsavel.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell }) => <span>{cell.getValue()}</span>,
			},
			{
				header: "Prazo",
				muiTableBodyCellProps: {
					align: "center",
				},
				filterVariant: "date",
				accessorKey: "deadline",
				widthPixels: getColumnWidth("prazo"),
				minWidthPixels: COLUMN_WIDTHS.prazo.min,
				maxWidthPixels: COLUMN_WIDTHS.prazo.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				accessorFn: (originalRow) => {
					const date = moment(originalRow.deadline);
					return date;
				},
				Cell: ({ cell, row }) => {
					const deadlineDate = moment(cell.getValue()); // Data do deadline (pode ser alterada)
					const originalDeadlineDate = moment(
						row.original.original_deadline,
					); // Prazo original (não pode ser alterado)
					const updateDate = moment(row.original.update_at);
					const today = moment();
					const status = row.original.status;

					let cellStyle = {
						padding: "3.5px",
						textAlign: "center",
						width: "100%",
					};

					if (status === "OK") {
						// Se o status for "OK", verifica se a data de atualização é após o prazo original
						if (updateDate.isAfter(originalDeadlineDate, "day")) {
							cellStyle = {
								...cellStyle,
								color: "white",
								backgroundColor: "red",
							};
						}
					} else if (status === "ANDAMENTO") {
						// Se o status for "ANDAMENTO", verifica se o prazo original já passou
						const isPastOriginalDeadline =
							originalDeadlineDate.isBefore(today, "day");

						cellStyle = {
							...cellStyle,
							color: isPastOriginalDeadline ? "white" : "inherit",
							backgroundColor: isPastOriginalDeadline
								? "red"
								: "inherit",
						};
					}

					return (
						<div style={cellStyle}>
							{deadlineDate.format("DD/MM/YYYY")}
						</div>
					);
				},
			},
			{
				accessorKey: "status",
				header: "Status",
				filterFn: (row, id, filterValue) => {
					// Se não houver filtro selecionado, exibe todas as linhas
					if (!filterValue || filterValue.length === 0) return true;
					// Verifica se o status da linha está nos valores selecionados
					return filterValue.includes(row.getValue(id));
				},
				widthPixels: getColumnWidth("status"),
				minWidthPixels: COLUMN_WIDTHS.status.min,
				maxWidthPixels: COLUMN_WIDTHS.status.max,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("alignContentCenter"),
				filterVariant: "custom",
				Filter: ({ column }) => {
					const currentFilter = column.getFilterValue() || [];
					const [open, setOpen] = React.useState(false);
					const customSelectStyles = getFilterFieldProps("custom");

					return (
						<FormControl
							{...customSelectStyles}
							sx={{
								...customSelectStyles.sx,
							}}
						>
							<Select
								multiple
								open={open}
								onOpen={() => setOpen(true)}
								onClose={() => setOpen(false)}
								value={currentFilter}
								onChange={(e) => {
									const value = e.target.value;
									// Se não há valores selecionados, remove o filtro completamente
									column.setFilterValue(value.length === 0 ? undefined : value);
								}}
								input={
									<OutlinedInput
										size="small"
										endAdornment={
											<InputAdornment
												position="end"
												sx={{ margin: "0px" }}
											>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														column.setFilterValue(undefined);
													}}
													edge="end"
													sx={{
														padding: "0px",
														margin: "0px",
														"&:hover": {
															backgroundColor:
																"transparent",
														},
													}}
												>
													<CloseIcon fontSize="small" />
												</IconButton>
												<IconButton
													size="small"
													onClick={() =>
														setOpen(!open)
													}
													edge="end"
													sx={{
														padding: "0px",
														margin: "0px",
														"&:hover": {
															backgroundColor:
																"transparent",
														},
													}}
												>
													{open ? (
														<ArrowDropUpIcon />
													) : (
														<ArrowDropDownIcon />
													)}
												</IconButton>
											</InputAdornment>
										}
										sx={{
											backgroundColor: "white",
											borderRadius: "3px",
											height: "28px",
											minHeight: "28px",
											fontSize: "0.75rem",
											paddingRight: "0px !important",
											"& .MuiOutlinedInput-input": {
												padding: "2px 6px",
												paddingRight: "0px !important",
												fontSize: "0.75rem",
											},
											"& .MuiSelect-icon": {
												display: "none",
											},
											"& .MuiSelect-select": {
												padding: "2px 6px",
												paddingRight: "0px !important",
												fontSize: "0.75rem",
											},
										}}
									/>
								}
								IconComponent={null}
								renderValue={(selected) => (
									<Box
										sx={{
											display: "flex",
											flexWrap: "nowrap",
											gap: 0.5,
											py: "2px",
										}}
									>
										{selected.map((value) => (
											<Chip
												key={value}
												label={value}
												size="small"
												onDelete={null}
												deleteIcon={null}
												sx={{
													fontSize: "0.75rem",
													height: "24px",
												}}
											/>
										))}
									</Box>
								)}
								MenuProps={{
									PaperProps: {
										sx: {
											maxHeight: 300,
											"& .MuiMenuItem-root": {
												minHeight: "32px",
											},
										},
									},
								}}
							>
								{[
									{ label: "ANDAMENTO", value: "ANDAMENTO" },
									{ label: "OK", value: "OK" },
									{ label: "STANDBY", value: "STANDBY" },
									{ label: "CANCELADO", value: "CANCELADO" },
								].map((option) => (
									<MenuItem
										key={option.value}
										value={option.value}
										sx={{
											fontSize: "0.875rem",
											padding: "4px 12px",
										}}
									>
										<Checkbox
											size="small"
											checked={currentFilter.includes(
												option.value,
											)}
											sx={{ padding: "0 8px 0 0" }}
										/>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					);
				},
				Cell: ({ cell }) => {
					const statusMap = {
						ANDAMENTO: {
							label: "Em Andamento",
							icon: "bx bx-right-arrow",
							color: "#556ee6",
						},
						STANDBY: {
							label: "Standby",
							icon: "bx bx-stop-circle",
							color: "#74788d",
						},
						OK: {
							label: "Ok",
							icon: "bx bx-like",
							color: "#34c38f",
						},
						CANCELADO: {
							label: "Cancelado",
							icon: "bx bx-block",
							color: "#f46a6a",
						},
					};

					const statusValue = cell.getValue();
					const status = statusMap[statusValue] || {};

					return (
						<Tooltip title={status.label} placement="top">
							<IconButton disableRipple style={{ padding: 0 }}>
								<i
									className={status.icon}
									style={{
										color: status.color,
										fontSize: "18px",
									}}
								/>
							</IconButton>
						</Tooltip>
					);
				},
			},
			{
				accessorKey: "actions",
				widthPixels: getColumnWidth("actions"),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<>
						{podeVisualizar && (
							<div style={{
								display: "flex",
								flexWrap: "wrap", // Permite quebra de linha
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								padding: "2px",
							}}>
								<div>
									<Tooltip
										title="Alterar prazo"
										placement="top"
									>
										<IconButton
											onClick={() =>
												changePrazoItem(row.original)
											}
											style={{
												background: "none",
												border: "none",
												padding: "1px", // Padding menor
												cursor: "pointer",
												minWidth: "auto",
												width: "auto",
												height: "auto",
											}}
										>
											<CalendarBlank
												size={14} // Ícone menor
												color="black"
											/>
										</IconButton>
									</Tooltip>
								</div>
								{row.original.status !== "OK" && (
									<div>
										<Tooltip title="Ok" placement="top">
											<IconButton
												onClick={() =>
													changeModalFinish(
														row.original,
													)
												}
												style={{
													background: "none",
													border: "none",
													padding: "1px", // Padding menor
													cursor: "pointer",
													minWidth: "auto",
													width: "auto",
													height: "auto",
												}}
											>
												<Check
													size={14} // Ícone menor
													color="black"
												/>
											</IconButton>
										</Tooltip>
									</div>
								)}
								<div>
									<Tooltip title="Excluir" placement="top">
										<IconButton
											onClick={() =>
												excluirItem(row.original)
											}
											style={{
												background: "none",
												border: "none",
												padding: "1px", // Padding menor
												cursor: "pointer",
												minWidth: "auto",
												width: "auto",
												height: "auto",
											}}
										>
											<Trash size={14} color="black" /> {/* Ícone menor */}
										</IconButton>
									</Tooltip>
								</div>
							</div>
						)}
					</>
				),
				align: "center",
			},
		],
		[podeVisualizar, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const changeModalFinish = (rowData) => {
		setDataModalFinish(rowData);
		setShowModalFininsh(true);
	};

	const changePrazoItem = (rowData) => {
		setChangePrazo(rowData);
		setModalChangePrazo(true);
	};

	const editarItem = (rowData) => {
		console.log(rowData);
		setModal(rowData);
		setShowEditModal(true);
	};

	const excluirItem = (rowData) => {
		setModal(rowData);
		setShowModal(true);
	};

	// Closes
	function onCloseModal() {
		setShowModal(false);
		getItems();
	}

	function onCloseModalFinish() {
		setShowModalFininsh(false);
		getItems();
	}

	function onCloseEditModal() {
		setShowEditModal(false);
		getItems();
	}

	function onCloseChangePrazoModal() {
		setModalChangePrazo(false);
		getItems();
	}

	function closeModalsOnChangePrazo() {
		setModalChangePrazo(false);
		setShowEditModal(false);
		getItems();
	}

	const getItems = async () => {
		try {
			if (!id) return;
			const response = await ataEncaminhamentosService.getListById(id);

			// Filtrar e ordenar encaminhamentos periódicos
			const periodicEncaminhamentos = response
				.filter((item) => item.periodic === "1") // Filtrar encaminhamentos periódicos
				.sort((a, b) => {
					// Primeiro ordenar por deadline
					const deadlineA = new Date(a.deadline);
					const deadlineB = new Date(b.deadline);
					if (deadlineA.getTime() !== deadlineB.getTime()) {
						return deadlineA - deadlineB;
					}
					// Se o deadline for igual, ordenar por create_at
					return new Date(a.create_at) - new Date(b.create_at);
				});

			// Filtrar e ordenar encaminhamentos não periódicos
			const nonPeriodicEncaminhamentos = response
				.filter((item) => item.periodic === "0") // Filtrar encaminhamentos não periódicos
				.sort((a, b) => {
					// Primeiro ordenar por deadline
					const deadlineA = new Date(a.deadline);
					const deadlineB = new Date(b.deadline);
					if (deadlineA.getTime() !== deadlineB.getTime()) {
						return deadlineA - deadlineB;
					}
					// Se o deadline for igual, ordenar por create_at
					return new Date(a.create_at) - new Date(b.create_at);
				});

			// Combinar os resultados: periódicos primeiro, depois não periódicos
			const organizedEncaminhamentos = [
				...periodicEncaminhamentos,
				...nonPeriodicEncaminhamentos,
			];

			setData(organizedEncaminhamentos);
		} catch (error) {
			console.log(error);
		}
	};

	const getAll = async () => {
		setIsLoading(true);
		await getItems();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, [id]);

	const handleFullScreenChange = (state) => {
		setIsFullScreen(state);
		console.log("Modo tela cheia:", state);
	};

	return (
		<Loading isLoading={isLoading}>
			<div>
				<Row>
					<Col
						md={12}
						style={{
							display: "flex",
							justifyContent: "end",
							marginBottom: 10,
							marginTop: 10,
						}}
					>
						<Button
							onClick={() => {
								const baseUrl =
									window.location.origin +
									window.location.pathname;
								let url = `${baseUrl}#/print/pdf/ata?id=${id}`;
								if (dataInTable) {
									console.log(JSON.stringify(dataInTable));
									// stringify?
									url += `&data=${JSON.stringify(
										dataInTable,
									)}`;
									console.log(url);
								}
								window.open(url, "_blank");
							}}
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
								marginLeft: 10,
							}}
						>
							<Printer size={16} /> Imprimir
						</Button>
						{podeVisualizar && (
							<Button
								onClick={() => editarItem(id)}
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									marginLeft: 10,
								}}
							>
								<Plus size={16} color="black" /> Novo
								Encaminhamento
							</Button>
						)}
					</Col>
				</Row>
				<div style={{ marginTop: 10 }}>
					<MaterialTableContainer
						columns={columns}
						data={data}
						setDataInTable={setDataInTable}
						initialState={{
							columnFilters: [
								{ id: "status", value: ["ANDAMENTO"] },
							],
						}}
						onFullScreenChange={handleFullScreenChange}
						renderTopToolbarCustomActions={({ table }) => (
							<Box
								sx={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									width: "100%",
									gap: "5px",
								}}
							>
								{table.getFilteredRowModel().rows.length > 0 && (
									<Box sx={{ fontWeight: "500", color: "#495057" }}>
										Total <span style={{ color: "#000" }}>{table.getFilteredRowModel().rows.length}</span> itens.
									</Box>
								)}
								<Tooltip
									title={
										<div>
											<p>Destaque vermelho na coluna Status: significa que o vencimento é uma data passada, que pode ser para qualquer status. Exceto para casos de status OK, com a data do OK, igual ao da data de vencimento ou anterior.</p>
											<p>Destaque amarelo: significa que o item da Ata é uma ação periódica.</p>
										</div>
									}
									placement="bottom-end"
								>
									<IconButton
										size="medium"
										style={{
											background: "none",
											cursor: "pointer",
											padding: "0px",
											margin: "0px",
											minWidth: "32px",
										}}
									>
										<InfoIcon fontSize="medium" />
									</IconButton>
								</Tooltip>
							</Box>
						)}
					/>
					{podeVisualizar && (
						<div
							style={{
								position: "fixed",
								top: "50%",
								transform: "translate(-119%, -50%)",
								zIndex: 1000,
								backgroundColor: "white",
							}}
						>
							<Tooltip
								title="Novo encaminhamento"
								placement="top"
							>
								<IconButton
									style={{
										background: "none",
										cursor: "pointer",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										border: "2px solid black",
										borderRadius: 4,
										padding: 4,
										width: 28,
										height: 28,
									}}
									onClick={() => editarItem(id)}
								>
									<Plus size={16} weight="bold" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Atualizar" placement="top">
								<IconButton
									style={{
										background: "none",
										cursor: "pointer",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										border: "2px solid black",
										borderRadius: 4,
										padding: 4,
										width: 28,
										height: 28,
										marginTop: 10,
									}}
									onClick={() => {
										getAll();
									}}
								>
									<ArrowsClockwise size={16} weight="bold" />
								</IconButton>
							</Tooltip>
						</div>
					)}
					{podeVisualizar && isFullScreen && (
						<Button
							onClick={() => editarItem(id)}
							style={{
								position: "fixed",
								top: 6,
								left: 10,
								zIndex: 1500,
								background: "none",
								padding: 3,
								cursor: "pointer",
								color: "black",
								fontSize: "12px",
							}}
						>
							<Plus size={12} color="black" /> Novo Encaminhamento
						</Button>
					)}

					{showModal && (
						<ModalExclude
							show={showModal}
							onCloseClick={onCloseModal}
							data={modal}
						/>
					)}
					{showEditModal && (
						<ModalCreateEdit
							show={showEditModal}
							onCloseClick={onCloseEditModal}
							data={modal}
							changePrazoItem={changePrazoItem}
							style={{ zIndex: 2000, background: "black" }}
						/>
					)}
					{modalChangePrazo && changePrazo && (
						<ModalChangePrazo
							show={modalChangePrazo}
							onCloseClick={onCloseChangePrazoModal}
							closeModalsOnChangePrazo={closeModalsOnChangePrazo}
							data={changePrazo}
						/>
					)}
					{showModalFininsh && (
						<ModalFinish
							show={showModalFininsh}
							onCloseClick={onCloseModalFinish}
							data={dataModalFinish}
						/>
					)}
				</div>
			</div>
		</Loading>
	);
};

export default Encaminhamentos;
